import { useLazyQuery, useQuery } from "@apollo/client";
import { Alert, Box, Skeleton, Stack, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import { IssueListButton } from "components/Forms/IssueListButton";
import { Issue, ShippableContainerItemFragment } from "generated-graphql/graphql";
import { useEffect, useMemo } from "react";
import { hasCriticalIssues } from "util/forms";

import { SHIPPABLE_CONTAINERS_QUERY, VALIDATE_UPDATE_SHIPMENT_LINES_INPUT } from "./api";
import { Shipment } from "./shipmentDetailTypes";
import { groupContainersByStream } from "./shippableContainerGrouping";
import { ShippableContainerGroup, ShippableContainerHeader } from "./ShippableContainerRow";
import { useContainerSelection } from "./useContainerSelection";

export function AddContainersDialog({
  shipment,
  stagedContainerIds,
  onStage,
  onClose,
}: {
  shipment: Shipment;
  /** Containers already on the shipment or already staged. */
  stagedContainerIds: string[];
  onStage: (containers: ShippableContainerItemFragment[]) => void;
  onClose: () => void;
}) {
  const { selectedIds, toggle, setSelection } = useContainerSelection();

  const { data, loading } = useQuery(SHIPPABLE_CONTAINERS_QUERY, {
    variables: { facilityId: shipment.facilityId },
    fetchPolicy: "cache-and-network",
  });
  const onShipment = useMemo(() => new Set(stagedContainerIds), [stagedContainerIds]);
  const candidates = useMemo(
    () => (data?.facility?.shippableContainers?.items ?? []).filter((c) => !onShipment.has(c.id)),
    [data?.facility?.shippableContainers?.items, onShipment]
  );

  // Live warn-level validation as the selection changes — e.g. a container
  // already on another open shipment warns here just like in the builder.
  const [validate, { data: validationData }] = useLazyQuery(VALIDATE_UPDATE_SHIPMENT_LINES_INPUT, {
    fetchPolicy: "no-cache",
  });
  useEffect(() => {
    if (selectedIds.size > 0) {
      validate({
        variables: { input: { shipmentId: shipment.id, addContainerIds: [...selectedIds] } },
      });
    }
  }, [selectedIds, shipment.id, validate]);
  const issues: Issue[] =
    selectedIds.size > 0 ? (validationData?.updateShipmentLinesInputValidator ?? []) : [];

  // Grouped by waste stream, the way the wizard and the contents grid both show
  // containers — the stream is what the vendor manifests, so it is what the
  // reader is deciding about.
  const groups = useMemo(() => groupContainersByStream(candidates), [candidates]);

  // Staged, not saved: the page has one Save, and adding a container should be
  // as undoable as any other edit on it.
  const handleAdd = () => {
    onStage(candidates.filter((c) => selectedIds.has(c.id)));
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      // Wider than the usual dialog: this now shows the same columns the
      // packaging wizard does, and at "sm" the location and deadline fell off
      // the right edge.
      maxWidth="md"
      fullWidth
      title="Add containers"
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Add",
        disabled: selectedIds.size === 0 || hasCriticalIssues(issues),
        onClick: handleAdd,
      }}
    >
      {loading && candidates.length === 0 && <Skeleton height={120} />}
      {!loading && candidates.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Every shippable container at this facility is already on the shipment.
        </Typography>
      )}
      <Stack sx={{ maxHeight: 360, overflowY: "auto" }}>
        {/*
          Sticky inside the scroller, not placed above it. The labels have to
          survive scrolling either way — the columns they name carry the meaning
          of every row and the stream headings between them don't repeat it. But
          sitting outside the scroller they kept their full width while the rows
          lost some to the scrollbar, so on any platform drawing a classic one
          the columns stopped lining up with the values beneath them. The wizard
          can place its header outside because its list has no inner scroller.
        */}
        {candidates.length > 0 && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              bgcolor: "background.paper",
            }}
          >
            <ShippableContainerHeader />
          </Box>
        )}
        {groups.map((group) => (
          <ShippableContainerGroup
            key={group.streamId || "uncharacterized"}
            group={group}
            selectedIds={selectedIds}
            onToggle={toggle}
            onToggleGroup={setSelection}
          />
        ))}
      </Stack>
      {issues.length > 0 && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {issues.map((issue, i) => (
            <Alert
              key={`${issue.key}-${i}`}
              severity={issue.level === "CRITICAL" ? "error" : "warning"}
            >
              {issue.message}
            </Alert>
          ))}
        </Stack>
      )}
    </Dialog>
  );
}
